import Dexie from 'dexie';
import moment from 'moment';
import {getUserLastCollectionDate} from '../../features/Personalization/service/indexDBSearchesService';
import {
    calcDaysDiff,
    getMostUsedPostZoomScreen
} from '../../features/Personalization/utils/personalization';
import {
    getMoreInfoEntity,
    addNewMoreInfoEntity,
    updateMoreInfoEntity
} from '../../features/Personalization/service/indexDBMoreInfoService';
import {isScreenInInfoMap} from '../../features/MoreInfo/utils/utils';
import {Templates, INFO_MAP} from '../../features/MoreInfo/constants';
import {isDevOrPrisoft} from '../../utils/platform';
import {configuration as baseConfiguration} from '../../features/Personalization/config';

interface CollectionDates {
    userName: string;
    collectionDates?: string[];
}

export interface User {
    userName: string;
    zoomFname: string;
}

export interface ScreenData {
    entityId: string;
    formName: string;
    subFormName: string;
    formTitle: string;
    template: Templates;
    hasAttachment?: boolean;
    targetColName?: string;
}

export interface IndexedForm {
    count: number;
    form: ScreenData;
}

export interface Entity extends User {
    collectionDate: string;
    screens?: IndexedForm[];
}

export const configuration = {
    ...baseConfiguration,
    days: isDevOrPrisoft && isDevOrPrisoft() ? 10 : 45,
    strongThreshold: 10,
    collectedActionTypes: 'F'
};

export class PersonalizedMoreInfo extends Dexie {
    public collectionDates: Dexie.Table<CollectionDates>;

    public screens: Dexie.Table<Entity>;

    constructor() {
        super('PersonalizedMoreInfo');
        this.version(1).stores({
            collectionDates: 'userName, collectionDates',
            screens: '[userName+zoomFname+collectionDate], screens'
        });
    }
}

export const db = new PersonalizedMoreInfo();

export const addScreenEntry = async ({
    zoomFname,
    formData
}: {
    zoomFname: string;
    formData: ScreenData;
}) => {
    const isIndexed = await isScreenInInfoMap(zoomFname, formData.entityId);
    if (isIndexed) {
        return;
    }
    const lastCollectionDate = await getUserLastCollectionDate(db);
    const entityToUpdate = await getMoreInfoEntity(db, zoomFname, lastCollectionDate);

    if (!entityToUpdate) {
        await addNewMoreInfoEntity(db, zoomFname, formData, lastCollectionDate);
    } else {
        await updateMoreInfoEntity(db, entityToUpdate, formData);
    }
};

const calculatedZoomFnames = [];

export const getMoreInfoScreenSuggestion = async (zoomFname: string) => {
    const shouldCalcSuggestion = !calculatedZoomFnames.includes(zoomFname);
    if (!shouldCalcSuggestion) {
        return null;
    }
    calculatedZoomFnames.push(zoomFname);
    const lastCollectionDate = await getUserLastCollectionDate(db);
    const entity = await getMoreInfoEntity(db, zoomFname, lastCollectionDate);
    if (entity) {
        const userCollectionDate = moment(lastCollectionDate, 'DD/MM/YYYY');
        const daysDiff = calcDaysDiff(userCollectionDate);
        const shouldExecute = daysDiff >= configuration.days;
        if (!shouldExecute) {
            return null;
        }
        const mostUsedScreen = await getMostUsedPostZoomScreen(entity, zoomFname);
        if (mostUsedScreen.count >= configuration.strongThreshold) {
            return mostUsedScreen;
        }
    }

    return null;
};

export const getCollectionPeriodData = async () => {
    const lastCollectionDate = await getUserLastCollectionDate(db);
    const infoMapKeys = Object.keys(INFO_MAP);
    try {
        const collectionEntitys = await Promise.all(
            infoMapKeys.map(key => {
                return getMoreInfoEntity(db, key, lastCollectionDate);
            })
        );
        const data = {};
        for (const key of infoMapKeys) {
            data[key] = [];
        }
        collectionEntitys.forEach(entity => {
            if (entity) {
                const screens = entity.screens.map(screen => screen.form);
                data[entity.zoomFname].push(...screens);
            }
        });
        return data;
    } catch (err) {
        console.log('An error occured while trying to get collection period data', err);
        return null;
    }
};
